import { useState, useMemo } from 'react';
import { Search, Plus, User, Shield, CreditCard, MoreVertical, Mail, Phone, Filter } from 'lucide-react';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import UserEditor from '../users/UserEditor';

const ROLE_LABELS = {
  tech: 'Technician',
  pm: 'Project Manager',
  admin: 'Administrator',
  demo: 'Demo User',
};

const ROLE_STYLES = {
  tech: { bg: colors.greenDim, color: colors.green },
  pm: { bg: colors.orangeDim, color: colors.orange },
  admin: { bg: colors.yellowDim, color: colors.yellow },
  demo: { bg: 'rgba(128, 128, 128, 0.15)', color: '#888888' },
};

const STATUS_STYLES = {
  active: { bg: colors.greenDim, color: colors.green },
  inactive: { bg: colors.redDim, color: colors.red },
  invited: { bg: colors.orangeDim, color: colors.orange },
};

export default function UsersTab({ permissions }) {
  const { getAllUsers, loading } = useData();

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Get filtered users
  const users = useMemo(() => {
    let result = getAllUsers();

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u =>
        (u.displayName || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term)
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      result = result.filter(u => u.role === roleFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(u => u.status === statusFilter);
    }

    return result;
  }, [getAllUsers, searchTerm, roleFilter, statusFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const allUsers = getAllUsers();
    return {
      total: allUsers.length,
      active: allUsers.filter(u => u.status === 'active').length,
      techs: allUsers.filter(u => u.role === 'tech').length,
      pms: allUsers.filter(u => u.role === 'pm').length,
      admins: allUsers.filter(u => u.role === 'admin').length,
    };
  }, [getAllUsers]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsCreating(false);
    setShowEditor(true);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsCreating(true);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedUser(null);
    setIsCreating(false);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            User Management
          </h1>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            Manage team members, roles, and card assignments
          </p>
        </div>
        <Button onClick={handleCreateUser}>
          <Plus size={18} />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{stats.total}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Total Users</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.green }}>{stats.active}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Active</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.green }}>{stats.techs}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Technicians</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.orange }}>{stats.pms}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>PMs</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.yellow }}>{stats.admins}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>Admins</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: colors.textMuted }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
              style={{
                background: colors.inputBg,
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
              }}
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={{
              background: colors.inputBg,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <option value="all">All Roles</option>
            <option value="tech">Technicians</option>
            <option value="pm">Project Managers</option>
            <option value="admin">Administrators</option>
            <option value="demo">Demo Users</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={{
              background: colors.inputBg,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="invited">Invited</option>
          </select>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  User
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Role
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Cards
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: colors.textMuted }}>
                  Last Login
                </th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8" style={{ color: colors.textMuted }}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map(user => {
                  const roleStyle = ROLE_STYLES[user.role] || ROLE_STYLES.tech;
                  const statusStyle = STATUS_STYLES[user.status] || STATUS_STYLES.inactive;
                  const cardCount = (user.assignedCards || []).length;

                  return (
                    <tr
                      key={user.uid}
                      className="cursor-pointer hover:opacity-80"
                      style={{ borderBottom: `1px solid ${colors.border}` }}
                      onClick={() => handleEditUser(user)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {user.photoUrl ? (
                            <img
                              src={user.photoUrl}
                              alt={user.displayName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ background: colors.inputBg }}
                            >
                              <User size={20} style={{ color: colors.textMuted }} />
                            </div>
                          )}
                          <div>
                            <p className="font-medium" style={{ color: colors.textPrimary }}>
                              {user.displayName || 'No name'}
                            </p>
                            <p className="text-sm" style={{ color: colors.textMuted }}>
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ background: roleStyle.bg, color: roleStyle.color }}
                        >
                          {ROLE_LABELS[user.role] || user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium capitalize"
                          style={{ background: statusStyle.bg, color: statusStyle.color }}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1" style={{ color: colors.textSecondary }}>
                          <CreditCard size={14} />
                          <span>{cardCount}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: colors.textMuted }}>
                        {user.lastLoginAt?.toDate
                          ? new Date(user.lastLoginAt.toDate()).toLocaleDateString()
                          : user.status === 'invited'
                            ? 'Never'
                            : '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditUser(user);
                          }}
                          className="p-2 rounded-lg hover:opacity-80"
                          style={{ color: colors.textMuted }}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* User Editor Modal */}
      {showEditor && (
        <UserEditor
          user={isCreating ? null : selectedUser}
          isNew={isCreating}
          onClose={handleCloseEditor}
        />
      )}
    </div>
  );
}
